@if (isTeacherEditing()) {
  @if (draft(); as draft) {
    <odl-dialog
      class="voice-recording-dialog"
      [label]="'QUESTIONS.VOICE_TITLE' | translate"
      [open]="true"
      (odl-close)="cancel()"
    >
      <odl-callout
        variant="info"
        class="voice-info-banner"
        [innerHTML]="
          'QUESTIONS.VOICE_RECORDING_INFO' | translate: { seconds: voiceRecordingMaxSeconds() }
        "
      ></odl-callout>

      <p class="voice-question-text">{{ questionText() }}</p>

      @if (canUsePronunciationAssessment() || competencies().length) {
        <section class="section">
          <div class="section-header">
            <h4>{{ 'QUESTIONS.SETTINGS' | translate }}</h4>
            <odl-button
              id="voice-settings-toggle"
              [attr.aria-expanded]="settingsExpanded()"
              [description]="(settingsExpanded() ? 'COLLAPSE' : 'EXPAND') | translate"
              (click)="toggleSettingsExpanded()"
            >
              <odl-icon
                name="expand_more"
                class="chevron"
                slot="only-icon"
                [class.chevron--expanded]="settingsExpanded()"
              ></odl-icon>
            </odl-button>
          </div>

          @if (settingsExpanded()) {
            <div class="settings">
              @if (canUsePronunciationAssessment()) {
                <div class="settings-row">
                  <odl-checkbox
                    [checked]="pronunciationAssessmentEnabled()"
                    [disabled]="isReadOnly()"
                    (odl-input)="togglePronunciationAssessment($event)"
                  >
                    {{ 'QUESTIONS.AUTOMATIC_GRADING_LABEL' | translate }}
                  </odl-checkbox>
                </div>
              }

              @if (competencies().length) {
                <div class="settings-row voice-skills">
                  <nr-competency-picker
                    [competencies]="competencies()"
                    [disabled]="isReadOnly()"
                    [selected]="selectedCompetencies()"
                    (selectedChange)="updateCompetencies($event)"
                  ></nr-competency-picker>
                </div>
              }
            </div>
          }
        </section>

        <odl-tooltip for="#voice-settings-toggle">
          {{ (settingsExpanded() ? 'COLLAPSE' : 'EXPAND') | translate }}
        </odl-tooltip>
      }

      <div slot="footer" class="dialog-footer">
        <div class="dialog-footer-start">
          @if (draft.id && canManageQuestions()) {
            <odl-button variant="text" [disabled]="saving()" (click)="confirmDelete.set(true)">
              {{ 'DELETE' | translate }}
            </odl-button>
          }
        </div>
        <div class="dialog-footer-end">
          <odl-button variant="secondary" [disabled]="saving()" (click)="cancel()">
            {{ 'CANCEL' | translate }}
          </odl-button>
          <odl-button variant="primary" [disabled]="saving() || isReadOnly()" (click)="save()">
            {{ (saving() ? 'SAVING' : draft.id ? 'SAVE' : 'CREATE') | translate }}
          </odl-button>
        </div>
      </div>
    </odl-dialog>
  }

  @if (confirmDelete()) {
    <odl-dialog
      class="voice-delete-dialog"
      [label]="'QUESTIONS.DELETE_TITLE_VOICE' | translate"
      [open]="true"
      (odl-close)="confirmDelete.set(false)"
    >
      {{ 'QUESTIONS.DELETE_BODY_VOICE' | translate }}
      <div slot="footer" class="dialog-footer">
        <odl-button variant="secondary" (click)="confirmDelete.set(false)">
          {{ 'CANCEL' | translate }}
        </odl-button>
        <odl-button variant="primary" (click)="confirmDeleteVoice()">
          {{ 'DELETE' | translate }}
        </odl-button>
      </div>
    </odl-dialog>
  }
} @else if (quiz(); as quiz) {
  <odl-dialog
    class="voice-recording-dialog"
    [label]="'QUESTIONS.VOICE_TITLE' | translate"
    [open]="true"
    (odl-close)="close()"
  >
    @switch (phase()) {
      @case ('answering') {
        <ng-container *ngTemplateOutlet="answeringBody"></ng-container>
        <div slot="footer" class="dialog-footer-end">
          <odl-button variant="secondary" (click)="close()">{{ 'CANCEL' | translate }}</odl-button>
          <odl-button variant="primary" [disabled]="!hasRecording()" (click)="submit()">
            {{ 'QUESTIONS.SEND' | translate }}
          </odl-button>
        </div>
      }

      @case ('submitting') {
        <ng-container *ngTemplateOutlet="answeringBody"></ng-container>
        <div slot="footer" class="dialog-footer-end">
          <odl-button variant="primary" [disabled]="true">
            {{ 'QUESTIONS.SEND' | translate }}
          </odl-button>
        </div>
      }

      @case ('pending') {
        <div class="voice-content">
          <ng-container *ngTemplateOutlet="statementBlock"></ng-container>
          @if (answeredAudioUrl(); as url) {
            <ng-container
              *ngTemplateOutlet="
                recordingBlock;
                context: { $implicit: url, durationMs: answeredDurationMs() }
              "
            ></ng-container>
          }
          <p class="voice-pending-review">
            {{
              (isPendingSync() ? 'QUESTIONS.GRADE_PENDING' : 'QUESTIONS.VOICE_PENDING_REVIEW_INFO')
                | translate
            }}
          </p>
        </div>
        <div slot="footer" class="dialog-footer-end">
          <odl-button variant="secondary" (click)="close()">{{ 'CANCEL' | translate }}</odl-button>
          <odl-button variant="primary" (click)="close()">{{ 'OK' | translate }}</odl-button>
        </div>
      }

      @case ('score') {
        <div class="question-card question-card--grade">
          <span class="grade-label">{{ 'QUESTIONS.VOICE_SCORE_LABEL' | translate }}</span>
          <span class="grade-value">{{ gradeOutOfTen() }}</span>
        </div>
        <div slot="footer" class="dialog-footer-end">
          <odl-button variant="secondary" (click)="close()">{{ 'CANCEL' | translate }}</odl-button>
          <odl-button variant="primary" (click)="seeAnswer()">
            {{ 'QUESTIONS.SEE_ANSWER' | translate }}
          </odl-button>
        </div>
      }

      @case ('watching') {
        <!-- Rendered here, not through statementBlock, so its anchors reach the tour. -->
        <nr-guided-tour
          [tourId]="pronunciationTourId"
          [steps]="pronunciationTourSteps"
          [enabled]="!isTeacher()"
        >
          <div class="voice-content">
            <!-- Each audio says whose it is: the sentence as it should sound, and the student's recording -->
            @if (pronunciationAssessment(); as assessment) {
              <div class="voice-block">
                <h4 class="voice-block-title">
                  {{ 'QUESTIONS.PRONUNCIATION.BLOCKS.MODEL' | translate }}
                </h4>
                <nr-pronunciation-feedback
                  [assessment]="assessment"
                  [text]="quiz.questions[0].text"
                  [questionId]="quiz.id"
                ></nr-pronunciation-feedback>
              </div>
            } @else {
              <p class="voice-question-text">{{ quiz.questions[0].text }}</p>
            }
            @if (answeredAudioUrl(); as url) {
              <div class="voice-block">
                <h4 class="voice-block-title">
                  {{ 'QUESTIONS.PRONUNCIATION.BLOCKS.RECORDING' | translate }}
                </h4>
                <nr-audio-player
                  nrTourAnchor="recording"
                  [src]="url"
                  [durationMs]="answeredDurationMs()"
                ></nr-audio-player>
              </div>
            }
            @if (pronunciationAssessment(); as assessment) {
              <nr-pronunciation-scores [assessment]="assessment"></nr-pronunciation-scores>
            }
          </div>
        </nr-guided-tour>
        <div slot="footer" class="dialog-footer-end">
          <odl-button variant="primary" (click)="back()">{{
            'QUESTIONS.BACK' | translate
          }}</odl-button>
        </div>
      }
    }
  </odl-dialog>

  <ng-template #answeringBody>
    <!-- Rendered inside the template, not around its outlet, so the anchors below reach the tour. -->
    <nr-guided-tour
      [tourId]="practiceTourId"
      [steps]="practiceTourSteps"
      [enabled]="!isTeacher() && phase() === 'answering'"
    >
      <div class="voice-content">
        <odl-callout
          variant="info"
          class="voice-info-banner"
          [innerHTML]="
            'QUESTIONS.VOICE_RECORDING_INFO' | translate: { seconds: voiceRecordingMaxSeconds() }
          "
        ></odl-callout>
        <div class="voice-block">
          <h4 class="voice-block-title">
            {{ 'QUESTIONS.PRONUNCIATION.BLOCKS.MODEL' | translate }}
          </h4>
          <div class="voice-question-listen">
            <nr-sentence-speech
              [text]="quiz.questions[0].text"
              [questionId]="quiz.id"
              [disabled]="isRecording() || phase() === 'submitting'"
            ></nr-sentence-speech>
            <p class="voice-question-text">{{ quiz.questions[0].text }}</p>
          </div>
        </div>

        @if (hasRecording()) {
          <p class="voice-hint">{{ 'QUESTIONS.LISTEN_AND_REVIEW' | translate }}</p>
        }

        @if (isRecording()) {
          <div class="voice-recording-status">
            <div class="voice-recording-indicator">
              <span class="recording-dot" aria-hidden="true"></span>
              <span class="recording-label">{{ 'QUESTIONS.RECORDING' | translate }}</span>
            </div>
            <p class="voice-time-left">
              {{ 'QUESTIONS.TIME_LEFT' | translate: { time: timeLeftDisplay() } }}
            </p>
          </div>
          <odl-button variant="secondary" (click)="stopRecording()">
            <odl-icon name="mic" slot="start"></odl-icon>
            {{ 'QUESTIONS.STOP_RECORDING' | translate }}
          </odl-button>
        } @else {
          <odl-button
            nrTourAnchor="record"
            variant="secondary"
            [disabled]="phase() === 'submitting'"
            (click)="startRecording()"
          >
            <odl-icon name="mic" slot="start"></odl-icon>
            {{ 'QUESTIONS.START_RECORDING' | translate }}
          </odl-button>
        }

        @if (recordedAudioUrl(); as url) {
          <ng-container
            *ngTemplateOutlet="
              recordingBlock;
              context: { $implicit: url, durationMs: recordedDurationMs() }
            "
          ></ng-container>
        }
      </div>
    </nr-guided-tour>
  </ng-template>

  <ng-template #statementBlock>
    @if (pronunciationAssessment(); as assessment) {
      <div class="voice-block">
        <h4 class="voice-block-title">{{ 'QUESTIONS.PRONUNCIATION.BLOCKS.MODEL' | translate }}</h4>
        <nr-pronunciation-feedback
          [assessment]="assessment"
          [text]="quiz.questions[0].text"
          [questionId]="quiz.id"
        ></nr-pronunciation-feedback>
      </div>
    } @else {
      <p class="voice-question-text">{{ quiz.questions[0].text }}</p>
    }
  </ng-template>

  <!-- The recording of the student, titled the same wherever it shows -->
  <ng-template #recordingBlock let-url let-durationMs="durationMs">
    <div class="voice-block">
      <h4 class="voice-block-title">
        {{ 'QUESTIONS.PRONUNCIATION.BLOCKS.RECORDING' | translate }}
      </h4>
      <nr-audio-player [src]="url" [durationMs]="durationMs"></nr-audio-player>
    </div>
  </ng-template>
}
